import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { compact, flatten, map, orderBy } from "lodash/fp";
import { DateTime } from "luxon";

import type { FeedItem } from "@app/analytics/Home/types";
import * as Parser from "@app/analytics/Home/utils/parsingUtils";
import apiClient from "@app/common/api/apiClient";
import useAuth from "@app/common/auth/useAuth";
import useHasMeetings from "@app/common/hooks/meetings/useHasMeetings";
import type {
  ApiActionItemClip,
  ApiGptHighlight,
  ApiKeyQuestionAnswerClip,
  User,
} from "@app/common/types";
import {
  getListFromPromiseResult,
  getValueFromPromiseResult,
} from "@app/utils/utils";

type DateRange = {
  startDate: DateTime;
  endDate: DateTime;
};

const EMPTY_CLIP_RESPONSE = { data: [] };

const getNewUserFeed = (user: User) => {
  const sampleReportForUser = Parser.parseSampleReport(user);
  return compact([sampleReportForUser]);
};

const getMeetingReports = async (dateRange: DateRange): Promise<FeedItem[]> => {
  const baseQuery = {
    ...dateRange,
    includeSummaryFields: true,
  };

  const [
    myMeetingsResponse,
    sharedMeetingsResponse,
    incompleteMeetingsResponse,
  ] = await Promise.allSettled([
    apiClient.sessions.listSessions(baseQuery),
    apiClient.sessions.listSessions({ ...baseQuery, view: "shared_with_me" }),
    apiClient.sessions.listSessions({ ...baseQuery, view: "incomplete" }),
  ]);

  const myMeetings = getListFromPromiseResult(myMeetingsResponse);
  const sharedMeetings = getListFromPromiseResult(sharedMeetingsResponse);
  const incompleteMeetings = getListFromPromiseResult(
    incompleteMeetingsResponse
  );

  const meetingReports = Parser.parseMeetingReports(myMeetings);
  const sharedMeetingReports = Parser.parseSharedMeetingReports(
    sharedMeetings,
    myMeetings
  );
  const incompleteMeetingReports =
    Parser.parseIncompleteMeetingReports(incompleteMeetings);

  return [
    ...meetingReports,
    ...sharedMeetingReports,
    ...incompleteMeetingReports,
  ];
};

const getActionItemsAndKeyQuestions = async (
  dateRange: DateRange
): Promise<FeedItem[]> => {
  const [actionItemResponse, keyQuestionResponse, keyQuestionAnswerResponse] =
    await Promise.allSettled([
      apiClient.clips.list({ ...dateRange, clipTypes: ["action_item"] }),
      apiClient.clips.list({ ...dateRange, clipTypes: ["key_question"] }),
      apiClient.clips.list({
        ...dateRange,
        clipTypes: ["key_question_answer"],
      }),
    ]);

  const actionItemClips = getValueFromPromiseResult(
    actionItemResponse,
    EMPTY_CLIP_RESPONSE
  ).data;

  const keyQuestionClips = getValueFromPromiseResult(
    keyQuestionResponse,
    EMPTY_CLIP_RESPONSE
  ).data;

  const keyQuestionAnswerClips = getValueFromPromiseResult(
    keyQuestionAnswerResponse,
    EMPTY_CLIP_RESPONSE
  ).data;

  const actionItemFeedItems = Parser.parseActionItems(
    actionItemClips as ApiActionItemClip[]
  );

  const keyQuestionAndAnswerFeedItems = Parser.parseKeyQuestionsAndAnswers(
    keyQuestionClips as ApiGptHighlight[],
    keyQuestionAnswerClips as ApiKeyQuestionAnswerClip[]
  );

  return [...actionItemFeedItems, ...keyQuestionAndAnswerFeedItems];
};

const EMPTY_FEED: FeedItem[] = [];

const getFeed = async () => {
  // TODO (ENG-9409) - handle updating date ranges when scrolling past first set of data,
  //  dynamically fetching more data if we don't have enough to fill a whole page
  const dateRange = {
    startDate: DateTime.now().minus({ days: 30 }).startOf("day"),
    endDate: DateTime.now().endOf("day"),
  };

  const promiseResultList = await Promise.allSettled([
    getMeetingReports(dateRange),
    getActionItemsAndKeyQuestions(dateRange),
  ]);

  const resultList = map(getListFromPromiseResult, promiseResultList);

  const results = flatten(resultList);
  return orderBy((r) => r.timestamp.toMillis(), "desc", results);
};

const useHomePageFeed = () => {
  const { user } = useAuth();

  const { isLoading: isHasMeetingsLoading, hasEstablishedMeetings } =
    useHasMeetings({ userId: user?.id });

  const {
    isError: isFeedError,
    isLoading: isFeedLoading,
    data: rawFeed,
  } = useQuery({
    queryKey: ["home", "feed", user?.id],
    queryFn: getFeed,
  });

  const data = rawFeed ?? EMPTY_FEED;
  const isLoading = isHasMeetingsLoading || isFeedLoading;

  const isNewUser = !isLoading && !hasEstablishedMeetings;

  const feed = useMemo(() => {
    if (isLoading || !user) return EMPTY_FEED;
    if (hasEstablishedMeetings) return data;

    return flatten([data, getNewUserFeed(user)]);
  }, [data, isLoading, hasEstablishedMeetings]);

  return {
    feed,
    isError: isFeedError,
    isLoading,
    isNewUser,
  };
};

export default useHomePageFeed;
